/// <reference types="google.maps" />
import {
  Component,
  ElementRef,
  ViewChild,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  signal,
  inject,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY } from 'rxjs';
import { InputTextModule } from 'primeng/inputtext';
import { MapsLoaderService } from '@shared';

@Component({
  selector: 'address-input',
  templateUrl: './address-input.component.html',
  imports: [InputTextModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    .googleplacessearch {
      background-color: #eee;
    }
    .loading {
      opacity: 0.6;
      pointer-events: none;
    }
  `,
})
export class AddressInputComponent implements OnInit, OnDestroy {
  @ViewChild('addrInput', { static: true })
  addrInput!: ElementRef<HTMLInputElement>;

  @Output() addressSelected =
    new EventEmitter<google.maps.places.PlaceResult>();

  private autocomplete: google.maps.places.Autocomplete | null = null;
  private listener: google.maps.MapsEventListener | null = null;
  private mapsLoader = inject(MapsLoaderService);
  private destroyRef = inject(DestroyRef);

  isLoading = signal(false);
  hasError = signal(false);

  ngOnInit() {
    this.initializeGooglePlaces();
  }

  private initializeGooglePlaces(): void {
    this.isLoading.set(true);
    this.hasError.set(false);

    this.mapsLoader
      .load()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => {
          console.error('Failed to load Google Maps API:', error);
          this.hasError.set(true);
          this.isLoading.set(false);
          return EMPTY;
        }),
      )
      .subscribe((placesLib) => {
        try {
          this.setupAutocomplete(placesLib);
          this.isLoading.set(false);
        } catch (error) {
          console.error('Failed to initialize autocomplete:', error);
          this.hasError.set(true);
          this.isLoading.set(false);
        }
      });
  }

  private setupAutocomplete(placesLib: any): void {
    this.autocomplete = new placesLib.Autocomplete(
      this.addrInput.nativeElement,
      {
        types: ['address'],
        componentRestrictions: {
          country: [
            'at',
            'be',
            'bg',
            'de',
            'hr',
            'cy',
            'cz',
            'dk',
            'ee',
            'fi',
            'fr',
            'de',
            'gr',
            'hu',
            'ie',
            'it',
            'lv',
            'lt',
            'lu',
            'mt',
            'nl',
            'pl',
            'pt',
            'ro',
            'sk',
            'si',
            'es',
            'se',
            'ch',
          ],
        }, // Restrict to EU countries + Switzerland
      },
    );

    this.listener = this.autocomplete!.addListener('place_changed', () => {
      try {
        const place = this.autocomplete?.getPlace();
        if (this.isValidPlace(place)) {
          this.addressSelected.emit(place);
        }
      } catch (error) {
        console.error('Error handling place selection:', error);
      }
    });
  }

  private isValidPlace(
    place: google.maps.places.PlaceResult | undefined,
  ): place is google.maps.places.PlaceResult {
    return !!(
      place &&
      place.formatted_address &&
      place.geometry &&
      place.geometry.location
    );
  }

  ngOnDestroy() {
    this.cleanup();
  }

  private cleanup(): void {
    if (this.listener) {
      this.listener.remove();
      this.listener = null;
    }

    if (this.autocomplete) {
      // Clear the autocomplete instance
      this.autocomplete = null;
    }
  }
}
